import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../../utils/api';

const pick = (settings, key, fallback = '') =>
  (settings[key] ?? fallback) || fallback;

const AboutPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const suf = isAr ? '_ar' : '';

  useEffect(() => {
    settingsAPI.getPublic()
      .then(rows => setSettings(Object.fromEntries((rows || []).map(r => [r.setting_key, r.setting_value]))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  const v = (key, fallback) => pick(settings, key + suf, pick(settings, key, fallback));

  const title = v('about_title', isAr ? 'قصتنا' : 'Our Story');
  const intro = v('about_intro', isAr
    ? 'تأسست قايا الكويت بشغف لتقديم أجود أنواع العسل من جميع أنحاء العالم. نعمل مباشرةً مع النحالين في اليمن ونيوزيلندا والخليج للحصول على أنقى وأصدق أنواع العسل.'
    : "Qaya KW was founded with a passion for bringing the world's finest honeys to Kuwait. We work directly with beekeepers across Yemen, New Zealand, and the Gulf to source the most authentic, pure honeys available.");
  const imageUrl = settings.about_image_url || '';

  const sections = [1, 2, 3].map(i => ({
    title: v(`about_section${i}_title`, ''),
    body: v(`about_section${i}_body`, ''),
  })).filter(s => s.title || s.body);

  return (
    <div className="about-page" dir={isAr ? 'rtl' : 'ltr'}>
      <section className="about-hero">
        <div className="container about-hero__inner">
          <div className="about-hero__copy">
            <h1>{title}</h1>
            {intro && <p>{intro}</p>}
          </div>
          {imageUrl && (
            <div className="about-hero__image">
              <img src={imageUrl} alt={title} />
            </div>
          )}
        </div>
      </section>

      {sections.length > 0 && (
        <section className="about-sections container">
          {sections.map((s, idx) => (
            <article key={idx} className="about-section">
              {s.title && <h2>{s.title}</h2>}
              {s.body && s.body.split(/\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </article>
          ))}
        </section>
      )}

      <style>{`
        .about-page { padding: 30px 0 80px; }
        .about-hero { padding: 30px 0 50px; }
        .about-hero__inner {
          display: grid; grid-template-columns: ${imageUrl ? '1fr 1fr' : '1fr'};
          gap: 50px; align-items: center;
        }
        .about-hero__copy h1 {
          font-family: var(--font-serif); font-size: 2.6rem;
          color: var(--olive, #4a5e2a); margin-bottom: 20px;
        }
        .about-hero__copy p {
          color: var(--text-muted); font-size: 1.05rem;
          line-height: 1.85; max-width: 560px;
        }
        .about-hero__image {
          aspect-ratio: 4 / 3; border-radius: var(--radius-lg);
          overflow: hidden; background: var(--cream);
        }
        .about-hero__image img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .about-sections {
          display: grid; gap: 36px; margin-top: 30px;
        }
        .about-section {
          background: var(--white); border-radius: var(--radius-lg);
          padding: 36px;
        }
        .about-section h2 {
          font-family: var(--font-serif); font-size: 1.55rem;
          color: var(--olive, #4a5e2a); margin-bottom: 14px;
        }
        .about-section p {
          color: var(--text-muted); line-height: 1.8;
          margin-bottom: 12px; font-size: 0.98rem;
        }
        .about-section p:last-child { margin-bottom: 0; }
        @media (max-width: 768px) {
          .about-hero__inner { grid-template-columns: 1fr; gap: 24px; }
          .about-hero__copy h1 { font-size: 2rem; }
          .about-section { padding: 24px; }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
